import { CreditCard, Plus, ArrowUpRight, ArrowDownLeft, Shield, Smartphone, Globe, MoreVertical } from 'lucide-react';
import { cn } from '../../../utils/helpers';

export default function Payment() {
  return (
    <div className="space-y-8">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Payments</h1>
          <p className="text-neutral-500 dark:text-neutral-400">Manage your payment methods and transactions.</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-neutral-900 text-white rounded-xl text-sm font-medium hover:bg-neutral-800 transition-colors shadow-lg shadow-neutral-900/20">
          <Plus className="w-4 h-4" />
          Add Method
        </button>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Payment Methods */}
        <div className="lg:col-span-2 space-y-6">
          <h2 className="text-xl font-bold">Your Cards</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-neutral-900 rounded-[2rem] p-8 text-white relative overflow-hidden group cursor-pointer shadow-2xl shadow-neutral-900/20">
              <div className="relative z-10 flex flex-col h-full justify-between min-h-[180px]">
                <div className="flex justify-between items-start">
                  <div className="w-12 h-8 bg-white/20 rounded-md backdrop-blur-md"></div>
                  <CreditCard className="w-8 h-8 text-white/50" />
                </div>
                <div>
                  <p className="text-xl font-mono tracking-[0.2em] mb-4">•••• •••• •••• 4242</p>
                  <div className="flex justify-between items-end">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-white/50">Card Holder</p>
                      <p className="font-bold">ALEX JOHNSON</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-white/50">Expires</p>
                      <p className="font-bold">12/25</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -right-12 -top-12 w-48 h-48 bg-white/5 rounded-full blur-3xl group-hover:bg-white/10 transition-colors"></div>
            </div>

            <div className="bg-white dark:bg-neutral-900 rounded-[2rem] p-8 border-2 border-dashed border-neutral-200 dark:border-neutral-800 flex flex-col items-center justify-center gap-4 hover:border-neutral-900 transition-all cursor-pointer group">
              <div className="w-12 h-12 rounded-full bg-neutral-50 dark:bg-neutral-950 flex items-center justify-center group-hover:bg-neutral-900 group-hover:text-white transition-all">
                <Plus className="w-6 h-6" />
              </div>
              <p className="font-bold text-neutral-500 dark:text-neutral-400 group-hover:text-neutral-900 dark:group-hover:text-neutral-50">Add New Card</p>
            </div>
          </div>

          <div className="space-y-4 pt-4">
            <h2 className="text-xl font-bold">Quick Actions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { label: 'Secure Pay', icon: Shield, desc: 'Encrypted payments' },
                { label: 'Mobile Pay', icon: Smartphone, desc: 'Pay with phone' },
                { label: 'Global Transfer', icon: Globe, desc: 'Send worldwide' },
              ].map((item) => (
                <div key={item.label} className="bg-white dark:bg-neutral-900 p-6 rounded-3xl border border-neutral-200 dark:border-neutral-800 hover:border-neutral-900 transition-all cursor-pointer group">
                  <div className="w-10 h-10 rounded-xl bg-neutral-50 dark:bg-neutral-950 flex items-center justify-center mb-4 group-hover:bg-neutral-900 group-hover:text-white transition-all">
                    <item.icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold text-sm">{item.label}</h3>
                  <p className="text-[10px] text-neutral-500 dark:text-neutral-400 mt-1">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Transaction History Sidebar */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Recent Activity</h2>
            <button className="text-sm font-bold text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50">Filter</button>
          </div>
          <div className="bg-white dark:bg-neutral-900 rounded-[2rem] border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm dark:shadow-none">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="p-4 flex items-center justify-between border-b border-neutral-50 last:border-0 hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-colors cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
                    {i % 2 === 0 ? <ArrowDownLeft className="w-5 h-5 text-emerald-600 dark:text-emerald-400" /> : <ArrowUpRight className="w-5 h-5 text-neutral-600 dark:text-neutral-300" />}
                  </div>
                  <div>
                    <p className="text-sm font-bold">Transaction {i}</p>
                    <p className="text-[10px] text-neutral-500 dark:text-neutral-400">Oct {20 - i}, 2023</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={cn("text-sm font-bold", i % 2 === 0 ? "text-emerald-600 dark:text-emerald-400" : "text-neutral-900 dark:text-neutral-50")}>
                    {i % 2 === 0 ? '+' : '-'}${ (Math.random() * 1000).toFixed(2) }
                  </p>
                  <button className="p-1 rounded-full hover:bg-neutral-200 dark:hover:bg-neutral-800"><MoreVertical className="w-3 h-3 text-neutral-400 dark:text-neutral-500" /></button>
                </div>
              </div>
            ))}
            <button className="w-full p-4 text-sm font-bold text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-colors">
              View All Transactions
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
